import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useTravelArticles } from "@/hooks/useTravelArticles";
import { getDefaultThumbnail } from "@/lib/strapi";

const TRAVEL_PREVIEW_LIMIT = 3;

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export function SwimTravel() {
  const { articles, isLoading, error } = useTravelArticles(TRAVEL_PREVIEW_LIMIT);
  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const getArticlePath = (article) => `/travel/${article?.slug || article?.id}`;

  return (
    <section
      data-testid="swim-travel-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12 md:mb-16">
          <p className="font-inter font-light text-xs tracking-[0.25em] uppercase text-warm-gray mb-3">
            Swim-Travel
          </p>
          <h2
            data-testid="travel-section-title"
            className="font-inter font-semibold text-3xl md:text-4xl lg:text-5xl tracking-tighter text-near-black mb-4"
          >
            Where Will You Swim Next?
          </h2>
          <p className="font-inter font-normal text-base md:text-lg text-warm-gray max-w-xl leading-relaxed">
            Hand-picked swim destinations, heritage routes, and tropical escapes across APAC.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {isLoading && (
            <div className="md:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray border border-near-black/[0.08]">
              Loading travel destinations...
            </div>
          )}

          {!!error && !isLoading && articles.length === 0 && (
            <div className="md:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-red-600 border border-near-black/[0.08]">
              Unable to load travel destinations right now. Please try again shortly.
            </div>
          )}

          {!isLoading && !error && articles.length === 0 && (
            <div className="md:col-span-3 bg-white rounded-sm p-6 text-sm font-inter text-warm-gray border border-near-black/[0.08]">
              No travel destinations found.
            </div>
          )}

          {!isLoading && articles.map((article) => {
            return (
              <motion.div key={article.id} variants={itemVariants}>
                <Link
                  to={getArticlePath(article)}
                  data-testid={`travel-card-${article.id}`}
                  className="group bg-white rounded-sm overflow-hidden border border-near-black/[0.08] hover:-translate-y-2 transition-transform duration-300 block"
                >
                  <div className="relative overflow-hidden aspect-[16/11]">
                    <img
                      src={article.image}
                      alt={article.title}
                      loading="lazy"
                      onError={handleImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-solar-orange text-near-black font-inter font-semibold text-[11px] tracking-[0.1em] uppercase px-3 py-1 rounded-sm">
                      {article.category || "Travel"}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="font-inter font-semibold text-xl text-near-black tracking-tight mb-3 group-hover:text-amber-dusk transition-colors duration-150">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-2 text-warm-gray mb-3">
                      <MapPin size={13} />
                      <span className="font-inter font-light text-xs">{article.location || "APAC"}</span>
                    </div>
                    <p className="font-inter font-normal text-sm text-warm-gray leading-relaxed mb-5 line-clamp-3">
                      {article.teaser}
                    </p>
                    <span className="inline-flex items-center gap-1 font-inter font-medium text-sm text-amber-dusk group-hover:gap-2 transition-all duration-150">
                      Explore
                      <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-16"
        >
          <Link
            to="/travel"
            data-testid="travel-discover-cta"
            className="inline-flex items-center gap-2 bg-near-black text-white font-inter font-semibold text-sm px-8 py-3.5 rounded-sm hover:bg-near-black/80 transition-colors duration-150"
          >
            Discover All Destinations
            <ArrowUpRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
